import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RotateCw } from 'lucide-react';
import { useT } from '@/i18n';
import { CLOSER_KEYS, closerApi, closerErrorStatus } from '@/features/closer/closer.api';
import type { NavigatorScope } from '@/features/closer/closer.types';

/**
 * La compuerta del rol navigator por `navigator_scope` (DISENO-CLOSER §1.2).
 *
 * El scope se lee del SERVIDOR en cada carga (`GET /navigator/closer/scope`),
 * no del token: a Jean le cambiaron el scope con la sesión abierta el 22-oct, y
 * un valor guardado en `auth.store` (de T2-D) habría seguido viejo.
 *
 * QUÉ PASA CON CADA RESPUESTA (m-b de la revisión Fable, §11.1 del plan):
 *  - `closer` → la pantalla del closer; `full` o `pool` → la de siempre;
 *  - **solo un 404** cuenta como `full`: es la API vieja, que todavía no tiene
 *    la ruta (web nueva + API vieja → todos siguen en `NavigatorShell`);
 *  - **cualquier otro error** (500, red, 403) → «Reintentar». NO se monta la
 *    pantalla de positivos a ciegas: a un closer le mostraría la agenda, las
 *    citas y el reloj que Edd le sacó.
 *
 * Con `mode="navigator"` y scope `closer`, redirige a `/closer`, salvo
 * `/navigator/cases/:id` y `/navigator/cases/:id/ea`, que quedan abiertas a
 * propósito (§1.3): por ahí se termina un caso que ya tenía trabajo.
 */
export function NavigatorScopeGate({ mode, children }: {
  mode: 'navigator' | 'closer';
  children: ReactNode;
}): JSX.Element {
  const loc = useLocation();
  const q = useQuery({
    queryKey: CLOSER_KEYS.scope,
    queryFn: closerApi.scope,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  let scope: NavigatorScope | null = null;
  if (q.data) {
    // Con un dato bueno en mano, un refresco fallido de fondo no tumba la pantalla.
    scope = q.data.scope;
  } else if (q.isError) {
    if (closerErrorStatus(q.error) === 404) scope = 'full';
    else return <ScopeRetry onRetry={() => { void q.refetch(); }} busy={q.isFetching} />;
  } else {
    return <GateLoader />;
  }

  if (mode === 'navigator') {
    if (scope === 'closer' && !isOpenCaseRoute(loc.pathname)) {
      return <Navigate to="/closer" replace />;
    }
    return <>{children}</>;
  }
  if (scope !== 'closer') return <Navigate to="/navigator" replace />;
  return <>{children}</>;
}

/** Las dos rutas del caso que el closer puede abrir (§1.3). */
export function isOpenCaseRoute(pathname: string): boolean {
  return /^\/navigator\/cases\/[^/]+(\/ea)?\/?$/.test(pathname);
}

function GateLoader(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-nav-bg">
      <Loader2 className="h-6 w-6 text-nav-teal animate-spin" aria-label="Loading" />
    </div>
  );
}

function ScopeRetry({ onRetry, busy }: { onRetry: () => void; busy: boolean }): JSX.Element {
  const t = useT();
  return (
    <div className="min-h-screen flex items-center justify-center bg-nav-bg px-4">
      <div
        role="alert"
        data-closer="gate-retry"
        className="w-full max-w-[420px] bg-white rounded-[14px] px-6 py-6 text-nav-navy font-body shadow-[0_1px_2px_rgba(18,43,68,.06),0_8px_24px_rgba(18,43,68,.10)]"
      >
        <p className="font-head font-bold text-[16px] m-0">{t('closer.gate.errorTitle')}</p>
        <p className="text-[14px] text-nav-slate mt-2 mb-4">{t('closer.gate.errorBody')}</p>
        <button
          type="button"
          onClick={onRetry}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-lg bg-nav-navy text-white font-head font-semibold text-sm hover:bg-nav-navyHover disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:outline-offset-2"
        >
          <RotateCw className="h-4 w-4" aria-hidden />
          {t('closer.retry')}
        </button>
      </div>
    </div>
  );
}
